import { useCallback, useEffect, useMemo, useState } from 'react';
import debounce from 'lodash/debounce';

const DEFAULT_DEBOUNCE_MS = 300;

export type UseDebouncedListSearchOptions = {
    debounceMs?: number;
};

export function useDebouncedListSearch(options: UseDebouncedListSearchOptions = {}) {
    const { debounceMs = DEFAULT_DEBOUNCE_MS } = options;
    const [inputValue, setInputValue] = useState('');
    const [activeSearch, setActiveSearch] = useState<string | undefined>(undefined);

    const debouncedCommit = useMemo(
        () =>
            debounce((raw: string) => {
                setActiveSearch(raw.trim() || undefined);
            }, debounceMs),
        [debounceMs]
    );

    useEffect(() => {
        return () => {
            debouncedCommit.cancel();
        };
    }, [debouncedCommit]);

    const reset = useCallback(() => {
        debouncedCommit.cancel();
        setInputValue('');
        setActiveSearch(undefined);
    }, [debouncedCommit]);

    const onInputChange = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            const value = e.target.value;
            setInputValue(value);
            if (value.trim()) {
                debouncedCommit(value);
            } else {
                debouncedCommit.cancel();
                setActiveSearch(undefined);
            }
        },
        [debouncedCommit]
    );

    return { inputValue, activeSearch, onInputChange, reset };
}
